import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { mdsSpacings } from "@/design-system/foundations";
import styled from "@emotion/styled";
import { FeedbackChip } from "@/components/messages/feedback/feedback-chip";
import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSModalDialog,
  MDSText,
  MDSTextField,
  MDSTextVariant,
} from "@/mds";

const NEGATIVE_FEEDBACK_OPTIONS = [
  "Don't like style",
  "Not factually correct",
  "Refused when it shouldn't have",
  "Harmful or unsafe",
  "Didn't reference personal notes",
  "Other",
];

interface MemItFeedbackModalProps {
  isModalOpen: boolean;
  handleCloseModal: () => void;
  isPositive: boolean;
  onSubmit: (suggestedFeedback: string | undefined, userText: string) => void;
}

export const MemItFeedbackModal = observer(
  ({ isPositive, onSubmit, handleCloseModal, isModalOpen }: MemItFeedbackModalProps) => {
    const [selectedFeedback, setSelectedFeedback] = useState<number>(-1);
    const [userText, setUserText] = useState<string>("");

    const wrappedHandleCloseModal = () => {
      setSelectedFeedback(-1);
      setUserText("");
      handleCloseModal();
    };

    const handleClickChip = (index: number) => {
      if (selectedFeedback === index) {
        // un-set the selected chip
        setSelectedFeedback(-1);
      } else {
        setSelectedFeedback(index);
      }
    };

    const handleChangeUserText = (value: string) => {
      setUserText(value);
    };

    const handleCancel = (e: React.MouseEvent) => {
      e.preventDefault();
      wrappedHandleCloseModal();
    };

    const handleSubmitFeedback = (e: React.FormEvent) => {
      e.preventDefault();
      setSelectedFeedback(-1);
      setUserText("");
      onSubmit(NEGATIVE_FEEDBACK_OPTIONS[selectedFeedback], userText);
    };

    return (
      <form onSubmit={handleSubmitFeedback}>
        <MDSModalDialog isOpen={isModalOpen} handleClose={wrappedHandleCloseModal}>
          <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
            Submit feedback
          </MDSText>

          <MDSDialogTextContent>
            {isPositive && (
              <FeedbackPrompt>Tell us more why this response was satisfying:</FeedbackPrompt>
            )}
            {!isPositive && (
              <>
                <FeedbackPrompt>Tell us more why this response was not satisfying:</FeedbackPrompt>
                <Chips>
                  {NEGATIVE_FEEDBACK_OPTIONS.map((text, i) => (
                    <FeedbackChip
                      key={text}
                      onClick={e => {
                        e.preventDefault();
                        handleClickChip(i);
                      }}
                      isSelected={selectedFeedback === i}
                    >
                      {text}
                    </FeedbackChip>
                  ))}
                </Chips>
              </>
            )}
            <FeedbackTextArea
              placeholder="Share more details below..."
              name="userText"
              isTextArea={true}
              textAreaProps={{
                rows: 5,
              }}
              value={userText}
              onChange={handleChangeUserText}
            />
          </MDSDialogTextContent>

          <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
            <MDSButton onClick={handleCancel}>Cancel</MDSButton>
            <MDSButton variant={MDSButtonVariant.Contrast} onClick={handleSubmitFeedback}>
              Submit
            </MDSButton>
          </MDSButtonGroup>
        </MDSModalDialog>
      </form>
    );
  }
);

const FeedbackTextArea = styled(MDSTextField)`
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  min-height: 72px;
  padding: ${({ theme }) => theme.spacing.sm};
  width: 100%;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`;

const Chips = styled.div({
  display: "flex",
  flexWrap: "wrap",
  gap: mdsSpacings().sm,
});

const FeedbackPrompt = styled(MDSText)`
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.colors.chat.header};
  margin: ${({ theme }) => theme.spacing.xs} 0 0 0;
  font-weight: ${({ theme }) => theme.fontWeights.regular};
`;
